import React from 'react'
import type {ReactNode} from 'react'

export const errorDescriptions: {
  [key: number]: {
    title: string
    description: ReactNode
  }
} = {
  400: {
    title: 'Bad Request',
    description: (
      <>
        <p>Oops! It seems your request was invalid.</p>
        <p>
          We couldn’t understand your request because it was formatted
          incorrectly or contained invalid data.
        </p>
        <p>
          Please double-check the information you provided and try again. If
          you’re unsure about what to do, you may find help in our Help Center
          or by contacting our support team at{' '}
          <a href="mailto:service@dowjones.com">service@dowjones.com</a>.
        </p>
      </>
    ),
  },
  403: {
    title: 'Forbidden',
    description: (
      <>
        <p>Access Denied: You don’t have permission to view this page.</p>
        <p>
          Unfortunately, you don’t have the necessary permissions to access the
          requested resource on our website. This could be due to a lack of
          proper authentication or restricted access.
        </p>
        <p>
          Make sure you’re logged in, or if you believe this is an error, please
          contact our support team at{' '}
          <a href="mailto:service@dowjones.com">service@dowjones.com</a> for
          further assistance.
        </p>
      </>
    ),
  },
  404: {
    title: 'Not Found',
    description: (
      <>
        <p>Page not found: The resource you’re looking for is missing.</p>
        <p>
          We couldn’t find the page or resource you were trying to access on our
          website. It’s possible that the link is broken or the content has been
          removed.
        </p>
        <p>
          Please double-check the web address for any typos or use our site’s
          search feature to find what you’re looking for.
        </p>
      </>
    ),
  },
  422: {
    title: 'Unprocessable Entity',
    description: (
      <>
        <p>Oops! We couldn’t process your request.</p>
        <p>
          Your request was well-formed, but it contained invalid data or
          parameters that our server was unable to process.
        </p>
        <p>
          Please review the information you provided and ensure it meets the
          required format and criteria. If you need assistance, you can find
          help in our Help Center or contact our support team at{' '}
          <a href="mailto:service@dowjones.com">service@dowjones.com</a>. We
          apologize for any inconvenience and appreciate your understanding.
        </p>
      </>
    ),
  },
  500: {
    title: 'Internal Server Error',
    description: (
      <>
        <p>Oops! Something went wrong on our end.</p>
        <p>
          We’re sorry, but it seems there’s been an error while trying to
          process your request. Our team has been notified, and we’re working to
          fix the issue as soon as possible.
        </p>
        <p>
          Please try refreshing the page or come back later. If the problem
          persists, don’t hesitate to contact our support team at{' '}
          <a href="mailto:service@dowjones.com">service@dowjones.com</a> or
          visit our Help Center. We apologize for any inconvenience and
          appreciate your patience.
        </p>
      </>
    ),
  },
}

export const defaultErrorDescription = {
  title: 'Oops! Something went wrong',
  description: (
    <>
      <p>
        We’re sorry, but it seems there’s been an error on our end while trying
        to process your request. Our team has been notified, and we’re working
        to fix the issue as soon as possible.
      </p>
      <p>In the meantime, you can try the following steps:</p>

      <ol>
        <li>
          Refresh the page: Give it a moment and click the refresh button on
          your browser, or press F5 (or Cmd + R on a Mac) to reload the page.
        </li>
        <li>
          Clear your browser’s cache and cookies: Sometimes, clearing your
          browser’s cache and cookies can resolve temporary issues.
        </li>
        <li>
          Check your internet connection: Ensure you have a stable internet
          connection, and try reloading the page.
        </li>
        <li>
          Wait and try again later: It’s possible that the issue is temporary,
          so please be patient and try accessing the website again in a few
          minutes.
        </li>
      </ol>
      <p>
        If the problem persists or you need further assistance, please don’t
        hesitate to contact our support team at{' '}
        <a href="mailto:service@dowjones.com">service@dowjones.com</a> or visit
        our Help Center.
      </p>
      <p>
        Thank you for your patience, and we apologize for any inconvenience
        caused.
      </p>
    </>
  ),
}
